import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userName = localStorage.getItem("user_name") || "Técnico";
  const userRole = localStorage.getItem("user_role") || "tecnico"; 
  const [openGroup, setOpenGroup] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // 🚀 Novo: Controle do menu mobile
  const isDarkPage = ['/faturamento', '/faturas', '/Custos'].includes(location.pathname);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_name");
    localStorage.removeItem("user_role");
    window.location.href = "/login";
  };

  const menuGroups = [
    { title: "Visão Geral", icon: "📊", items: [{ to: "/dashboard", label: "Painel" }, { to: "/calendario", label: "Agenda" }] },
    { title: "Serviços", icon: "🛠️", items: [{ to: "/maintenance", label: "Chamados Técnicos" }, {to: "/tasks", label: "tasks"}, { to: "/clients", label: "Clientes" }] },
    { title: "Parque de Máquinas", icon: "🖨️", items: [{ to: "/printers", label: "Alugadas" }, { to: "/laboratorio", label: "Em Estoque" }] },
    { title: "Suprimentos", icon: "📦", items: [{ to: "/toner", label: "Estoque & Peças" }, {to: "/supplies", label: "Peças a comprar" } ] },
  ];

  useEffect(() => {
    menuGroups.forEach(group => {
      if (group.items.some(item => item.to === location.pathname)) { setOpenGroup(group.title); }
    });
    setIsMobileMenuOpen(false); // 🚀 Fecha o menu mobile ao trocar de página
  }, [location.pathname]);

  const toggleGroup = (title) => setOpenGroup(openGroup === title ? null : title);

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-transparent font-sans text-slate-900 overflow-hidden text-[13px] print:bg-white">
      
      {/* 📱 HEADER MOBILE (Aparece apenas em telas pequenas) */}
      <header className="lg:hidden h-16 bg-slate-950 border-b border-slate-800 flex items-center justify-between px-6 z-30 shrink-0">
        <h2 className="text-xl font-black text-white tracking-tighter">Sistema<span className="text-indigo-500">Dash</span></h2>
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="text-white text-2xl focus:outline-none"
        >
          {isMobileMenuOpen ? "✕" : "☰"}
        </button>
      </header>

      {/* 🌑 OVERLAY MOBILE (Escurece o fundo quando o menu abre no celular) */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* aside lateral - Adaptada para Mobile e Desktop */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72 bg-slate-950 text-slate-300 flex flex-col border-r border-slate-800 shadow-2xl transition-transform duration-300 ease-in-out print:hidden
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
      `}>        
        <div className="h-20 shrink-0 flex items-center px-8 border-b border-slate-800/50">
          <h2 className="text-2xl font-black text-white tracking-tighter">Sistema<span className="text-indigo-500">Dash</span></h2>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto custom-scrollbar">
          {menuGroups
            .filter(group => !group.adminOnly || userRole === 'admin')
            .map((group, idx) => {
              const isOpen = openGroup === group.title;
              const hasActiveItem = group.items.some(item => item.to === location.pathname);
              return (
                <div key={idx} className="space-y-1">
                  <button onClick={() => toggleGroup(group.title)} className={`w-full flex items-center justify-between px-4 py-3 rounded-2xl transition-all font-black text-[11px] uppercase tracking-wider ${isOpen || hasActiveItem ? "text-white bg-slate-900/50" : "text-slate-500 hover:text-slate-300"}`}>
                    <div className="flex items-center gap-3"><span className="text-lg opacity-80">{group.icon}</span><span>{group.title}</span></div>
                    <span className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>▼</span>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
                    <div className="pl-10 space-y-1 mt-1">
                      {group.items.map((item) => (
                        <Link key={item.to} to={item.to} className={`block px-4 py-2.5 rounded-xl transition-all font-bold text-[11px] ${location.pathname === item.to ? "text-indigo-400 bg-indigo-500/5" : "text-slate-500 hover:text-indigo-300"}`}>
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </nav>

        <div className="p-4 border-t border-slate-800/50 bg-slate-900/50">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-9 h-9 shrink-0 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-black text-xs shadow-lg shadow-indigo-500/20">{userName.substring(0, 2).toUpperCase()}</div>
            <div className="overflow-hidden text-left">
              <p className="text-xs font-black text-white truncate uppercase">{userName}</p>
              <p className={`text-[9px] font-bold uppercase tracking-widest ${userRole === 'admin' ? 'text-emerald-500' : 'text-slate-400'}`}>
                {userRole === 'admin' ? 'Admin Online' : 'Técnico Operacional'}
              </p>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 hover:text-rose-400 hover:bg-rose-500/5 transition-all font-black text-[10px] uppercase tracking-[0.15em]"><span>🚪</span> Sair do Sistema</button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`flex-1 flex flex-col h-screen overflow-hidden print:overflow-visible print:h-auto relative transition-colors duration-500 ${isDarkPage ? 'bg-[#070913]' : 'bg-[#f8fafc]'}`}> 
        <div className="flex-1 overflow-y-auto print:p-0 print:overflow-visible custom-scrollbar">
          <div className="w-full h-full p-2 lg:p-0"> {/* 🚀 Adicionado um pequeno padding no mobile */}
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}